<template>
    <div class="upload-photo-container tw-min-h-64">
        <div
            v-for="(slot, index) in photoSlots"
            :key="index"
            class="photo-slot"
        >
            <Upload
                :ref="`upload_${index}`"
                :action="uploadUrl(index)"
                :headers="uploadHeaders"
                :on-success="res => handleSuccess(res, index)"
                :on-error="err => handleError(err, index)"
                :on-progress="evt => handleProgress(evt, index)"
                :before-upload="beforeUpload"
                :show-upload-list="false"
                accept="image/*"
            >
                <!-- Always render both; show preview on top when photo exists -->
                <div class="photo-preview" v-if="slot.photo">
                    <img :src="slot.photo" :alt="`Branch Photo ${index + 1}`" />
                    <div class="photo-overlay">
                        <Icon type="ios-camera" size="40" />
                        <p>{{ $t("branch.clickToChange") }}</p>
                    </div>
                </div>
                <div class="upload-placeholder" v-else>
                    <Icon type="ios-image-outline" size="60" />
                    <p>{{ $t("branch.clickToUpload") }}</p>
                    <Button
                        icon="ios-cloud-upload-outline"
                        :loading="slot.uploading"
                    >
                        {{
                            slot.uploading
                                ? $t("branch.uploading")
                                : $t("branch.selectPhoto")
                        }}
                    </Button>
                </div>
            </Upload>

            <div class="progress-bar" v-if="slot.uploading">
                <Progress :percent="slot.percent" status="active" />
            </div>
        </div>
    </div>
</template>

<script>
import VueCookies from "vue-cookies";

export default {
    name: "UploadPhoto",
    props: {
        branch: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            localBranch: {},
            photoSlots: [
                { photo: null, uploading: false, percent: 0 },
                { photo: null, uploading: false, percent: 0 },
                { photo: null, uploading: false, percent: 0 }
            ]
        };
    },
    computed: {
        uploadHeaders() {
            const token = VueCookies.get("token");
            return { Authorization: `Bearer ${token}` };
        }
    },
    mounted() {
        this.loadPhotos();
    },
    watch: {
        branch: {
            handler() {
                this.loadPhotos();
            },
            deep: true
        }
    },
    methods: {
        uploadUrl(index) {
            const b = this.internalBranch || this.branch;
            if (!b || !b.branch_id) return "";
            return `${process.env.VUE_APP_PROXY}/api/branches/${b.branch_id}/upload-certificate-photo?slot=${index}`;
        },
        loadPhotos() {
            // Sync internalBranch from prop on every load
            this.internalBranch = Object.assign({}, this.branch);

            const fields = [
                "branch_certificate_photo",
                "branch_certificate_photo_1",
                "branch_certificate_photo_2"
            ];

            this.photoSlots = fields.map(field => ({
                photo: this.toPhotoUrl(this.internalBranch?.[field]),
                uploading: false,
                percent: 0
            }));
        },
        // Convert a value to a usable photo URL.
        // The list API may return raw paths (e.g. "branch/abc.jpg") while
        // the upload response returns full URLs after $branch->refresh().
        toPhotoUrl(value) {
            if (!value) return null;
            if (value.startsWith("http")) return value;
            return `${process.env.VUE_APP_PROXY}/api/files?filePath=${value}`;
        },
        beforeUpload(file) {
            const isImage = file.type.startsWith("image/");
            const isLt5M = file.size / 1024 / 1024 < 5;
            if (!isImage) {
                this.$Message.error(this.$t("branch.mustBeImage"));
                return false;
            }
            if (!isLt5M) {
                this.$Message.error(this.$t("branch.imageTooLarge"));
                return false;
            }
            return true;
        },
        handleProgress(event, index) {
            this.photoSlots[index].uploading = true;
            this.photoSlots[index].percent = parseInt(event.percent);
        },
        handleSuccess(response, index) {
            this.photoSlots[index].uploading = false;
            this.photoSlots[index].percent = 0;

            if (response.success) {
                // Controller calls $branch->refresh() so all fields are full URLs
                const fieldMap = {
                    0: "branch_certificate_photo",
                    1: "branch_certificate_photo_1",
                    2: "branch_certificate_photo_2"
                };

                // Update every slot from its named field in the response
                [0, 1, 2].forEach(i => {
                    const url = this.toPhotoUrl(response.data?.[fieldMap[i]]);
                    this.photoSlots[i].photo = url;
                    if (this.internalBranch) {
                        this.internalBranch[fieldMap[i]] = url;
                    }
                });

                this.$Message.success(this.$t("branch.uploadSuccess"));
                this.$emit("on-success", response.data);
            } else {
                this.$Message.error(
                    response.message || this.$t("branch.uploadFailed")
                );
            }
        },
        handleError(error, index) {
            this.photoSlots[index].uploading = false;
            this.photoSlots[index].percent = 0;
            this.$Message.error(this.$t("branch.uploadFailed"));
            console.error(`Upload error (slot ${index}):`, error);
        }
    }
};
</script>

<style scoped lang="scss">
.upload-photo-container {
    padding: 10px;

    .photo-slot {
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 0;
        }

        .photo-preview {
            position: relative;
            cursor: pointer;
            border-radius: 4px;
            overflow: hidden;

            img {
                width: 100%;
                max-height: 400px;
                display: block;
                object-fit: contain;
            }

            .photo-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0, 0, 0, 0.5);
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                opacity: 0;
                transition: opacity 0.3s;

                i {
                    color: #fff;
                    margin-bottom: 10px;
                }
                p {
                    color: #fff;
                    font-size: 14px;
                    margin: 0;
                }
            }

            &:hover .photo-overlay {
                opacity: 1;
            }
        }

        .upload-placeholder {
            padding: 42px 20px;
            background: #f8f8f9;
            border: 2px dashed #dcdee2;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.3s;
            text-align: center;

            &:hover {
                border-color: #2d8cf0;
                background: #f0f7ff;
            }

            i {
                color: #808695;
                margin-bottom: 10px;
            }
            p {
                margin: 10px 0;
                color: #515a6e;
                font-size: 14px;
            }
        }

        .progress-bar {
            margin-top: 8px;
        }
    }
}
</style>

<style lang="scss">
/* Force iView upload wrapper full width inside upload-photo-container */
.upload-photo-container .ivu-upload {
    display: block !important;
    width: 100% !important;
}
.upload-photo-container .ivu-upload-drag {
    width: 100% !important;
}
</style>
